import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { Textarea } from 'primeng/textarea';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengTextareaDirective,
  ValidationError,
} from '@web/shared/ui';
import { isEmptyString, isNil, Nillable, valueOrDefault } from '@web/utils';
import {
  AdminRefundSettingsFormData,
  AdminRefundSettingsFormGroup,
} from '../../models/admin-refund-settings-form-data';

const parseEmails = (value: Nillable<string>): string[] =>
  valueOrDefault(value, '')
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const emailPattern =
  /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i;

const codMessageEmailsValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent as AdminRefundSettingsFormGroup | null;
    const isCodEmailSent = parent?.controls.isCodEmailSent.value === true;

    if (!isCodEmailSent) {
      return null;
    }

    const value = control.value;

    if (
      isNil(value) ||
      isEmptyString(value) ||
      parseEmails(value).length === 0
    ) {
      return {
        [ValidationError.Required]: true,
      };
    }

    const invalidEntry = parseEmails(value).find(
      (entry) =>
        entry.length > AdminRefundSettingsFormData.CodMessageEmailMaxLength ||
        !emailPattern.test(entry),
    );

    if (isNil(invalidEntry)) {
      return null;
    }

    return {
      [ValidationError.Custom]: {
        message: 'Wprowadź poprawne adresy e-mail, po jednym w osobnej linii.',
      },
    };
  };
};

@Component({
  selector: 'app-admin-refund-settings-form',
  imports: [
    Button,
    CommonModule,
    FormControlComponent,
    PrimengButtonDirective,
    PrimengTextareaDirective,
    ReactiveFormsModule,
    Textarea,
    ToggleSwitch,
  ],
  templateUrl: './admin-refund-settings-form.component.html',
  styleUrl: './admin-refund-settings-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminRefundSettingsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  public readonly data = input.required<AdminRefundSettingsFormData>();
  public readonly submitClickEvent = output<AdminRefundSettingsFormData>();

  protected readonly form = this.buildForm();

  public ngOnInit(): void {
    this.fillByData();
    this.bindCodEmailSentChanges();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private toData(): AdminRefundSettingsFormData {
    const data = this.form.getRawValue();

    return new AdminRefundSettingsFormData({
      isCodEmailSent: valueOrDefault(data.isCodEmailSent, false),
      codMessageEmails: parseEmails(data.codMessageEmails),
    });
  }

  private buildForm(): AdminRefundSettingsFormGroup {
    return this.builder.group({
      isCodEmailSent: this.builder.control<boolean | null>(null),
      codMessageEmails: this.builder.control<Nillable<string>>(null, [
        codMessageEmailsValidator(),
      ]),
    });
  }

  private bindCodEmailSentChanges(): void {
    this.form.controls.isCodEmailSent.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.controls.codMessageEmails.updateValueAndValidity({
          emitEvent: false,
        });
      });
  }

  private fillByData(): void {
    this.form.patchValue(
      {
        isCodEmailSent: this.data().isCodEmailSent,
        codMessageEmails: this.data().codMessageEmails.join('\n'),
      },
      { emitEvent: false },
    );
  }
}
